import { useMemo, useEffect, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { has, keys as _keys, get as _get, Dictionary } from 'lodash';
import { eachDayOfInterval, format } from 'date-fns';

import type {
  ReservationServiceResourceView,
  CustomSlot,
  Portal,
} from 'models';
import { eventDuration } from 'utils';
import { PortalsName } from 'utils/enums';

import {
  Button,
  Card,
  CardActions,
  Slide,
  CardContent,
  ClickAwayListener,
  LinearProgress,
} from '@material-ui/core';
import Skeleton from '@mui/material/Skeleton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import {
  Aside,
  ServicesCalendarPicker,
  ButtonShape,
  BorderedBox,
  NoData,
  ServicesCalendarDayPicker,
  TimeIcon,
  ClockIcon,
  LabelIcon,
} from 'components';

import clsx from 'clsx';
import { useStyles } from './style';

interface Props {
  portal: Portal | null;
  date: Date;
  from: Date;
  to: Date;
  loading: boolean;
  upload: boolean;
  slotDuration: number | null;
  daysSlots: Dictionary<Dictionary<CustomSlot>>;
  reservationResource: ReservationServiceResourceView | null;
  handleChangeDate: (date: Date | null) => void;
  handleReserveSlot: (slot: CustomSlot) => Promise<void>;
  handleCancelSlotReservation: (slot: CustomSlot) => Promise<void>;
}

const ServicesCalendarView: React.FC<Props> = ({
  portal,
  date,
  from,
  to,
  loading,
  upload,
  daysSlots,
  slotDuration,
  reservationResource,
  handleChangeDate,
  handleReserveSlot,
  handleCancelSlotReservation,
}) => {
  const history = useHistory();
  const params = useParams();

  const mainURL = useMemo(() => {
    return portal?.Name === PortalsName.Default
      ? '/services'
      : '/maintenance/booking-services';
  }, [portal]);

  const week = eachDayOfInterval({ start: from, end: to });

  const classes = useStyles({ count: week.length });
  const { t } = useTranslation();

  const [openCard, setOpenCard] = useState<CustomSlot | null>(null);

  useEffect(() => {
    if (has(params, 'id')) {
      const id = _get(params, 'id');

      if (id !== reservationResource?.Id) {
        history.push(mainURL);
      }
    }
    /* eslint-disable-next-line */
  }, [mainURL]);

  const forwardToServices = () => {
    history.push(mainURL);
  };

  const handleSlotClick = (e: MouseEvent<HTMLElement>, slot: CustomSlot) => {
    setOpenCard(slot);
  };

  const handleClickAway = () => {
    setOpenCard(null);
  };

  const handleReserve = async () => {
    if (openCard) {
      await handleReserveSlot(openCard);

      handleClickAway();
    }
  };

  const handleCancelReservation = async () => {
    if (openCard) {
      await handleCancelSlotReservation(openCard);

      handleClickAway();
    }
  };

  const days = useMemo(() => {
    return week.map((day) => {
      const key = format(day, "yyyy-MM-dd'T'00:00:00");
      const slots = _get(daysSlots, key);

      if (loading && !slots) {
        return (
          <div className={classes.loaderWrapper}>
            <Skeleton className={classes.skeleton} />
          </div>
        );
      }

      return (
        <ul key={day.toDateString()} className={classes.ul}>
          {_keys(slots).map((interval) => {
            const slot = _get(slots, interval);
            const isDisabled = !slot.CanCancel && !slot.CanReserve;

            return (
              <li
                onClick={
                  isDisabled ? undefined : (e) => handleSlotClick(e, slot)
                }
                key={slot.StartTime}
                className={clsx(classes.slot, {
                  [classes.disabledSlot]: isDisabled,
                })}
              >
                {slot.DisplayText || interval}
              </li>
            );
          })}
        </ul>
      );
    });
    /* eslint-disable-next-line */
  }, [week, daysSlots]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Button classes={{ root: classes.button }} onClick={forwardToServices}>
          <ChevronLeftIcon className={classes.icon} />
          <span className={classes.title}>{t('Booking')}</span>
        </Button>
      </div>
      {!reservationResource ? (
        <NoData />
      ) : (
        <div className={classes.container}>
          <Aside className={classes.aside}>
            <ServicesCalendarPicker
              className={classes.picker}
              date={date}
              handleChangeDate={handleChangeDate}
            />
            <BorderedBox className={classes.card}>
              <span className={classes.cardTitle}>
                {reservationResource.Name}
              </span>
              {reservationResource.ImagesUrl.length &&
              reservationResource.ImagesUrl[0] ? (
                <img
                  className={classes.cardImg}
                  src={
                    reservationResource.ImagesUrl.length
                      ? reservationResource.ImagesUrl[0]
                      : ''
                  }
                  alt={reservationResource.Name || ''}
                />
              ) : (
                <span className={classes.imgMock}></span>
              )}
              <span className={classes.cardPrice}>
                {reservationResource.SlotPrice?.Value
                  ? ` ${eventDuration(slotDuration)} / ${
                      reservationResource.SlotPrice?.Symbol
                    } ${reservationResource.SlotPrice?.Value} `
                  : t('Free')}
              </span>
              <span className={classes.cardDescription}>
                {reservationResource.Description}
              </span>
            </BorderedBox>
          </Aside>
          <div className={classes.wrapper}>
            <div className={classes.box}>
              <ServicesCalendarDayPicker
                date={date}
                week={week}
                handleChange={handleChangeDate}
              />
              <div className={classes.days}>{days}</div>

              <Slide in={Boolean(openCard)} direction="left" unmountOnExit>
                <div className={classes.cardWrapper}>
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Card classes={{ root: classes.cardRoot }}>
                      {upload ? (
                        <LinearProgress
                          classes={{
                            colorPrimary: classes.progressColorPrimary,
                            barColorPrimary: classes.barColorPrimary,
                          }}
                        />
                      ) : (
                        <div className={classes.mock}></div>
                      )}
                      <CardContent className={classes.cardInfoContent}>
                        <ul className={classes.cardInfo}>
                          <li className={classes.cardInfoItem}>
                            <ClockIcon className={classes.cardInfoItemIcon} />
                            <span className={classes.cardInfoItemText}>
                              {openCard?.StartTime
                                ? format(new Date(openCard?.StartTime), 'HH:mm')
                                : null}
                              {' - '}
                              {openCard?.EndTime
                                ? format(new Date(openCard?.EndTime), 'HH:mm')
                                : null}
                            </span>
                          </li>
                          <li className={classes.cardInfoItem}>
                            <LabelIcon className={classes.cardInfoItemIcon} />
                            <span className={classes.cardInfoItemText}>
                              {reservationResource.SlotPrice?.Symbol}{' '}
                              {reservationResource.SlotPrice?.Value}
                            </span>
                          </li>
                          <li className={classes.cardInfoItem}>
                            <TimeIcon className={classes.cardInfoItemIcon} />
                            <span className={classes.cardInfoItemText}>
                              {eventDuration(slotDuration)}
                            </span>
                          </li>
                        </ul>
                      </CardContent>
                      <CardActions className={classes.cardActions}>
                        <ButtonShape
                          className={classes.closeBtn}
                          onClick={handleClickAway}
                        >
                          {t('Cancel')}
                        </ButtonShape>
                        {openCard?.CanReserve ? (
                          <ButtonShape
                            onClick={handleReserve}
                            className={clsx(classes.acceptBtn, {
                              [classes.disabledBtn]: upload,
                            })}
                            disabled={upload}
                          >
                            {t('#portal.reservation.label.createreservation')}
                          </ButtonShape>
                        ) : null}
                        {openCard?.CanCancel ? (
                          <ButtonShape
                            onClick={handleCancelReservation}
                            className={clsx(classes.cancelBtn, {
                              [classes.disabledBtn]: upload,
                            })}
                            disabled={upload}
                          >
                            {t('#portal.reservation.label.cancelreservation')}
                          </ButtonShape>
                        ) : null}
                      </CardActions>
                    </Card>
                  </ClickAwayListener>
                </div>
              </Slide>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesCalendarView;
